import React, { ReactNode } from 'react';
import logoAsset from '../../../assets/images/logo.png';

// ── Loading Widget ────────────────────────────────────────────
export const LoadingWidget: React.FC = () => (
  <div className="loading-overlay">
    <div className="loading-box">
      <div className="spinner" />
    </div>
  </div>
);

// ── Shimmer ──────────────────────────────────────────────────
interface ShimmerProps {
  width?: string | number;
  height?: string | number;
  borderRadius?: string;
  style?: React.CSSProperties;
}
export const ShimmerAnimation: React.FC<ShimmerProps> = ({
  width = '100%',
  height = 16,
  borderRadius = '6px',
  style,
}) => (
  <div
    className="shimmer"
    style={{ width, height, borderRadius, ...style }}
  />
);

// ── Zero State ────────────────────────────────────────────────
interface ZeroStateProps {
  title: string;
  message: string;
  icon?: ReactNode;
}
export const ZeroState: React.FC<ZeroStateProps> = ({ title, message, icon }) => (
  <div className="zero-state">
    {icon || (
      <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="32" cy="32" r="28" fill="var(--color-primary-container)" />
        <path d="M22 32h20M32 22v20" stroke="var(--color-on-primary-container)" strokeWidth="2.5" strokeLinecap="round" opacity="0.4"/>
      </svg>
    )}
    <h3 style={{ fontSize: 16, fontWeight: 700 }}>{title}</h3>
    <p>{message}</p>
  </div>
);

// ── ZeroState1 (same appearance) ─────────────────────────────
export const ZeroState1 = ZeroState;

// ── Field Label ───────────────────────────────────────────────
interface FieldLabelProps { label: string }
export const FieldLabel: React.FC<FieldLabelProps> = ({ label }) => (
  <span className="field-label">{label.toUpperCase()}</span>
);

// ── Field Error ───────────────────────────────────────────────
interface FieldErrorProps { message: string }
export const FieldError: React.FC<FieldErrorProps> = ({ message }) => (
  <div className="field-error">
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
    <span>{message}</span>
  </div>
);

// ── Field Wrapper ─────────────────────────────────────────────
interface FieldProps {
  label: string;
  error?: string | null;
  children: ReactNode;
}
export const Field: React.FC<FieldProps> = ({ label, error, children }) => (
  <div>
    <FieldLabel label={label} />
    {children}
    {error && <FieldError message={error} />}
  </div>
);

// ── Dialog ────────────────────────────────────────────────────
interface DialogProps {
  open: boolean;
  onClose?: () => void;
  children: ReactNode;
}
export const Dialog: React.FC<DialogProps> = ({ open, onClose, children }) => {
  if (!open) return null;
  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog" onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
};

// ── Bottom Sheet ──────────────────────────────────────────────
interface BottomSheetProps {
  open: boolean;
  onClose?: () => void;
  children: ReactNode;
}
export const BottomSheet: React.FC<BottomSheetProps> = ({ open, onClose, children }) => {
  if (!open) return null;
  return (
    <>
      <div className="bottom-sheet-overlay" onClick={onClose} />
      <div className="bottom-sheet">{children}</div>
    </>
  );
};

// ── AppBar ────────────────────────────────────────────────────
interface AppBarProps {
  title?: string;
  onBack?: () => void;
  actions?: ReactNode;
  transparent?: boolean;
  style?: React.CSSProperties;
}
export const AppBar: React.FC<AppBarProps> = ({ title, onBack, actions, transparent, style }) => (
  <div className="appbar" style={{ background: transparent ? 'transparent' : undefined, boxShadow: transparent ? 'none' : undefined, ...style }}>
    {onBack && (
      <button
        onClick={onBack}
        style={{ background: 'none', padding: 4, display: 'flex', alignItems: 'center', color: transparent ? 'var(--color-on-surface)' : 'white' }}
        aria-label="Back"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>
    )}
    {title && <span className="appbar-title" style={transparent ? { color: 'var(--color-on-surface)' } : undefined}>{title}</span>}
    {actions}
  </div>
);

// ── App logo (brand asset) ───────────────────────────────────
export const LogoSvg: React.FC<{ size?: number; white?: boolean; style?: React.CSSProperties }> = ({
  size = 80,
  white,
  style,
}) => (
  <img
    src={logoAsset}
    alt="TherApp Connect"
    width={size}
    height={size}
    style={{
      display: 'block',
      objectFit: 'contain',
      ...(white ? { filter: 'brightness(0) invert(1)' } : {}),
      ...style,
    }}
  />
);

// ── Fade In Animation ─────────────────────────────────────────
interface FadeInProps {
  delay?: number;
  children: ReactNode;
  style?: React.CSSProperties;
}
export const FadeInAnimation: React.FC<FadeInProps> = ({ delay = 0, children, style }) => (
  <div
    className="fade-in-up"
    style={{ animationDelay: `${delay * 0.3}s`, ...style }}
  >
    {children}
  </div>
);

// ── Credits Pill ──────────────────────────────────────────────
interface CreditsPillProps { credits: number }
export const CreditsPill: React.FC<CreditsPillProps> = ({ credits }) => (
  <span style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    padding: '3px 8px',
    background: 'var(--color-primary-container)',
    borderRadius: 99,
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--color-on-primary-container)',
  }}>
    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M21 18v1a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v1h-9a2 2 0 00-2 2v8a2 2 0 002 2h9z"/><path d="M23 13v-2a1 1 0 00-1-1h-8a1 1 0 00-1 1v4a1 1 0 001 1h8a1 1 0 001-1v-2z"/></svg>
    {credits} credit{credits !== 1 ? 's' : ''}
  </span>
);

// ── Status Chip ───────────────────────────────────────────────
interface StatusChipProps { status: string }
const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  COMPLETED: { bg: 'rgba(107,186,107,0.15)', text: '#22C55E' },
  CANCELLED: { bg: 'rgba(239,68,68,0.12)', text: '#EF4444' },
  EXPIRED:   { bg: 'rgba(107,114,128,0.12)', text: '#6B7280' },
  PENDING:   { bg: 'rgba(245,158,11,0.12)', text: '#F59E0B' },
};
export const StatusChip: React.FC<StatusChipProps> = ({ status }) => {
  const s = (status || '').toUpperCase();
  const { bg, text } = STATUS_COLORS[s] || { bg: 'var(--color-primary-container)', text: 'var(--color-primary)' };
  return (
    <span style={{ padding: '4px 10px', borderRadius: 99, background: bg, color: text, fontSize: 12, fontWeight: 600 }}>
      {s.charAt(0) + s.slice(1).toLowerCase()}
    </span>
  );
};
